import React, { useState } from 'react'
import Modal from '../../../components/modal/modal'
import CloseSVG from '../../../assets/svg/close.svg'
import Button from '../../../components/button'
import { useForm } from 'react-hook-form'
import { formValidation } from '../../../utils/functions'
import FormInput from '../../../components/form-input'
import FormTextArea from '../../../components/form-textarea'
import FormSwitch from '../../../components/form-switch'
import AsyncSelect from 'react-select/async'
import DOMPurify from 'dompurify'
import {
  useAddGoal,
  useEditGoalsInfo,
} from '../../../redux/human-resources/hook/performance'
import { employees as employeeAPI } from '../../../api/hr/employee'
import { store } from '../../../redux'
import { setAlert } from '../../../redux/components/components-slice'
import ConfirmationModal from '../../../components/modal/confirmation-modal'

const AddGoal = ({ closeModal, isOpen, type, existingGoal }) => {
  const {
    register,
    handleSubmit,
    formState: { isValid },
    errors,
  } = useForm({
    mode: 'onChange',
    defaultValues: existingGoal
      ? {
          name: existingGoal.name,
          description: existingGoal.description,
          start_date: existingGoal.start_date,
          end_date: existingGoal.end_date,
        }
      : {},
  })

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [formData, setFormData] = useState(null)

  const { mutateAsync: addGoal, isLoading: addGoalLoading } = useAddGoal()
  const { mutateAsync: editGoal, isLoading: editLoading } = useEditGoalsInfo()

  const [owner, setOwner] = useState(
    existingGoal
      ? {
          label: `${existingGoal.owners[0]?.first_name} ${existingGoal.owners[0]?.last_name}`,
          value: existingGoal.owners[0]?.id,
          employee_id: existingGoal.owners[0]?.id,
        }
      : []
  )

  const [members, setMembers] = useState(
    existingGoal
      ? existingGoal.members.map((m) => ({
          label: `${m.first_name} ${m.last_name}`,
          value: m.id,
          employee_id: m.id,
        }))
      : []
  )

  const [subGoals, setSubGoals] = useState(
    existingGoal
      ? existingGoal.sub_goals.map((sg) => ({
          id: sg.id, // include id for existing ones
          name: sg.name,
          assignees: sg.assignees.map((a) => ({
            label: `${a.first_name} ${a.last_name}`,
            value: a.id,
            employee_id: a.id,
          })),
          target_date: sg.target_date,
          assignee_permission: sg.assignee_permission === 1,
        }))
      : [
          {
            name: '',
            assignees: [],
            target_date: '',
            assignee_permission: true,
          },
        ]
  )

  const loadOptions = (inputValue) =>
    new Promise((resolve) => {
      employeeAPI
        .getEmployees({
          limit: 10,
          statusFilter: 0,
          search: inputValue,
          page: 1,
        })
        .then((response) => {
          let data = []

          response?.data?.forEach((item) => {
            data.push({
              label: `${item.first_name} ${item.last_name}`,
              value: item.id,
              employee_id: item.id,
            })
          })

          resolve(data)
        })
        .catch((err) => {
          resolve([])
        })
    })

  const handleAddSubGoal = () => {
    setSubGoals([
      ...subGoals,
      {
        name: '',
        assignees: [],
        target_date: '',
        assignee_permission: true,
      },
    ])
  }

  const handleRemoveSubGoal = (index) => {
    const updated = [...subGoals]
    updated.splice(index, 1)
    setSubGoals(updated)
  }

  const updateSubGoal = (index, key, value) => {
    const updated = [...subGoals]
    updated[index][key] = value
    setSubGoals(updated)
  }

  const submitForm = async (data) => {
    const name = DOMPurify.sanitize(data?.name)
    const description = DOMPurify.sanitize(data?.description)
    const start_date = DOMPurify.sanitize(data?.start_date)
    const end_date = DOMPurify.sanitize(data?.end_date)

    const mainEndDate = new Date(end_date)
    for (let i = 0; i < subGoals.length; i++) {
      const subGoalDate = new Date(subGoals[i].target_date)
      if (subGoalDate > mainEndDate) {
        store.dispatch(
          setAlert(
            true,
            'warning',
            `Sub-goal ${
              i + 1
            } target date cannot be after the main goal's target date.`
          )
        )
        return
      }
    }

    const formattedSubGoals = subGoals.map((sg) => {
      const base = {
        name: sg.name,
        target_date: sg.target_date,
        assignee_permission: sg.assignee_permission,
        assignees: sg.assignees.map((a) => a.value),
      }
      if (sg.id) base.id = sg.id
      return base
    })

    const payload = {
      name,
      description,
      owners: owner ? [owner.value] : [],
      start_date,
      goal_type: type,
      end_date,
      members: members.map((m) => m.value),
      sub_goals: formattedSubGoals,
    }

    console.log(payload, 'payload')

    const ownerIds = Array.isArray(owner)
      ? owner.map((o) => o.value || o.id)
      : owner
      ? [owner.value || owner.id]
      : []
    const memberIds = members.map((m) => m.value || m.id)

    if (!memberIds.includes(ownerIds[0])) {
      store.dispatch(
        setAlert(
          true,
          'warning',
          'The selected goal owner must also be included as a member.'
        )
      )
      return
    }

    for (let s of subGoals) {
      const invalid = s.assignees.filter(
        (a) => !memberIds.includes(a?.value || a?.employee_id)
      )
      if (invalid.length > 0) {
        store.dispatch(
          setAlert(
            true,
            'warning',
            'Some sub-goal assignees are not part of the goal members.'
          )
        )
        return
      }
    }

    try {
      if (existingGoal) {
        await editGoal({ id: existingGoal.id, data: payload })
      } else {
        await addGoal(payload)
      }
      closeModal()
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <>
      {showConfirmation && (
        <ConfirmationModal
          isOpen={showConfirmation}
          closeModal={() => setShowConfirmation(false)}
          title={existingGoal ? 'Edit Goal' : 'Create a new Goal'}
          subtitle={`Are you sure you want to ${
            existingGoal ? 'update' : 'save'
          } this goal and its sub-goals?`}
          btnFunction={() => {
            submitForm(formData)
            setShowConfirmation(false)
          }}
        />
      )}
      <Modal
        scrollable={true}
        className='!max-w-[600px] !rounded-none'
        isOpen={isOpen}
        centered={true}
      >
        <form
          className='flex flex-col overflow-auto p-3'
          onSubmit={handleSubmit((data) => {
            setFormData(data)
            setShowConfirmation(true)
          })}
        >
          <div className='flex justify-between items-center '>
            <h2 className='text-lg font-semibold'>
              {existingGoal ? 'Edit Goal' : 'Add Goal'}
            </h2>
            <div className='cursor-pointer' onClick={() => closeModal()}>
              <img src={CloseSVG} alt='' />
            </div>
          </div>
          <hr className='divider' />

          <p className='text-sm text-black font-medium mb-4'>
            Unlock your potential and set a goal that ignites your company’s
            passion! You’re one step closer to making your dreams a reality.
          </p>

          <FormInput
            label='Goal Objective'
            name='name'
            placeholder='Name your goal'
            type='text'
            className='!bg-white !text-[14px]'
            inputRef={register(formValidation('text', true))}
            error={errors.name}
            errorMessage={errors.name && errors.name.message}
          />
          <FormTextArea
            label={'Description'}
            name='description'
            placeholder='Complete the app'
            className='!bg-white !text-[14px]'
            inputRef={register(formValidation('text'))}
            error={errors.description}
            errorMessage={errors.description && errors.description.message}
          />

          <div className='flex flex-col gap-4'>
            <div className='w-full'>
              <label className='!text-[13px] leading-[23px] font-normal text-color-gray'>
                Goal Owner
              </label>
              <AsyncSelect
                isMulti={false}
                cacheOptions
                defaultOptions
                className='text-[13px]'
                placeholder={'Type to select a goal owner'}
                loadOptions={loadOptions}
                onChange={(options) => setOwner(options)}
                classNamePrefix='mySelect'
                value={owner || null}
              />
            </div>
            <div className='flex items-center gap-4'>
              <div className='w-full'>
                <FormInput
                  label='Set Start Date'
                  name='start_date'
                  type='date'
                  inputRef={register(formValidation('date', true))}
                  error={errors.start_date}
                  errorMessage={errors.start_date && errors.start_date.message}
                />
              </div>
              <div className='w-full'>
                <FormInput
                  label='Set Target Date'
                  name='end_date'
                  type='date'
                  inputRef={register(formValidation('date', true))}
                  error={errors.end_date}
                  errorMessage={errors.end_date && errors.end_date.message}
                />
              </div>
            </div>
            <div className='w-full'>
              <label className='!text-[13px] leading-[23px] font-normal text-color-gray'>
                Add Members
              </label>
              <AsyncSelect
                isMulti
                cacheOptions
                defaultOptions
                className='text-[13px]'
                placeholder={'Add members'}
                loadOptions={loadOptions}
                onChange={(options) => setMembers(options)}
                classNamePrefix='mySelect'
                value={members || []}
              />
            </div>
          </div>

          <div className='flex flex-col mt-4 gap-3'>
            <div className='flex justify-between items-center'>
              <p className='font-medium text-base'>Sub Goals</p>
            </div>

            {subGoals.map((goal, index) => (
              <div
                key={index}
                className='border p-3 rounded-md flex flex-col gap-3'
              >
                <p className='text-sm font-medium'>Sub-goal {index + 1}</p>

                <div className='flex gap-4 w-full items-center'>
                  <div className='w-[70%]'>
                    <FormInput
                      label='Name Sub Goal'
                      name='name'
                      placeholder='Name your sub goal'
                      type='text'
                      className='!bg-white !text-[14px]'
                      value={goal.name}
                      onChange={(e) =>
                        updateSubGoal(index, 'name', e.target.value)
                      }
                    />
                  </div>
                  <div className='w-[30%]'>
                    <FormInput
                      label='Target Date'
                      name='target_date'
                      type='date'
                      value={goal.target_date}
                      onChange={(e) =>
                        updateSubGoal(index, 'target_date', e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className='w-full'>
                  <label className='!text-[13px] leading-[23px] font-normal text-color-gray'>
                    Assign to
                  </label>
                  <AsyncSelect
                    isMulti
                    cacheOptions
                    defaultOptions
                    className='text-[13px]'
                    placeholder={'Assign to team member(s)'}
                    loadOptions={loadOptions}
                    onChange={(selectedOptions) =>
                      updateSubGoal(index, 'assignees', selectedOptions)
                    }
                    classNamePrefix='mySelect'
                    value={goal.assignees || []}
                  />
                </div>
                <>
                  {subGoals.length > 1 && (
                    <div className='flex justify-end'>
                      <button
                        onClick={() => handleRemoveSubGoal(index)}
                        className='text-xs text-red-500 font-semibold hover:underline'
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </>
              </div>
            ))}

            <button
              type='button'
              onClick={handleAddSubGoal}
              className='flex items-center text-sm gap-2 font-medium hover:underline'
            >
              Add another sub-goal
            </button>
          </div>

          <Button
            text={existingGoal ? 'Update Goal' : 'Save'}
            type='submit'
            theme='primary'
            className='flex gap-2 h-[35px] w-fit px-3 py-2 mt-3'
            loading={existingGoal ? editLoading : addGoalLoading}
            textClass={'!text-[12px]'}
          />
        </form>
      </Modal>
    </>
  )
}

export default AddGoal
